import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import SendPendingDialog from "../components/SendPendingDialog";
import { Archive, DownloadSimple, Vault as VaultIcon, CheckCircle, Funnel, CalendarBlank, EnvelopeSimple, PaperPlaneTilt, Signature } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Vault() {
    const [data, setData] = useState({ cases: [], stats: { count: 0, total_settled_amount: 0, total_savings: 0 } });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [year, setYear] = useState("");
    const [bulkLoading, setBulkLoading] = useState(false);
    const [rowLoading, setRowLoading] = useState(null);

    // Digest controls
    const [digestOptIn, setDigestOptIn] = useState(true);
    const [digestSaving, setDigestSaving] = useState(false);
    const [digestSending, setDigestSending] = useState(null); // 'month'|'year'|null
    const [digestHistory, setDigestHistory] = useState([]);

    // Bulk sign-pending
    const [pending, setPending] = useState({ count: 0, preview: [] });
    const [pendingLoading, setPendingLoading] = useState(true);
    const [bulkSigning, setBulkSigning] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);

    const refreshPending = () => {
        setPendingLoading(true);
        api.get("/vault/pending-sign")
            .then((r) => setPending(r.data))
            .catch(() => setPending({ count: 0, preview: [] }))
            .finally(() => setPendingLoading(false));
    };

    useEffect(() => {
        api.get("/vault/cases")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
        api.get("/vault/digest/settings")
            .then((r) => setDigestOptIn(r.data.email_digests))
            .catch(() => {});
        api.get("/vault/digest/history")
            .then((r) => setDigestHistory(r.data))
            .catch(() => {});
        api.get("/vault/pending-sign")
            .then((r) => setPending(r.data))
            .catch(() => setPending({ count: 0, preview: [] }))
            .finally(() => setPendingLoading(false));
    }, []);

    const toggleDigest = async (next) => {
        setDigestSaving(true);
        try {
            await api.patch("/vault/digest/settings", { email_digests: next });
            setDigestOptIn(next);
            toast.success(next ? "Email digests enabled" : "Email digests disabled");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDigestSaving(false);
        }
    };

    const sendDigestNow = async (kind) => {
        setDigestSending(kind);
        try {
            const { data: res } = await api.post("/vault/digest/send", { kind });
            if (res.sent) {
                toast.success(`${kind === "month" ? "Monthly" : "Year-end"} digest sent — ${res.case_count} case${res.case_count === 1 ? "" : "s"} attached`);
                const hist = await api.get("/vault/digest/history");
                setDigestHistory(hist.data);
            } else {
                toast.info(`Nothing to send: ${res.reason}`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDigestSending(null);
        }
    };

    const years = Array.from(new Set(
        data.cases
            .map((c) => (c.settled_at || "").slice(0, 4))
            .filter(Boolean)
    )).sort().reverse();

    const filtered = data.cases.filter((c) => {
        const matchesQ = !q.trim() ||
            (c.title || "").toLowerCase().includes(q.toLowerCase()) ||
            (c.creditor_name || "").toLowerCase().includes(q.toLowerCase());
        const matchesYear = !year || (c.settled_at || "").startsWith(year);
        return matchesQ && matchesYear;
    });

    const downloadBlob = async (url, fallbackName) => {
        const res = await api.get(url, { responseType: "blob" });
        const disp = res.headers["content-disposition"] || "";
        const match = disp.match(/filename="([^"]+)"/);
        const name = match ? match[1] : fallbackName;
        const blob = new Blob([res.data], { type: "application/zip" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
    };

    const downloadCase = async (caseId, title) => {
        setRowLoading(caseId);
        try {
            await downloadBlob(`/vault/case/${caseId}.zip`, `${title}.zip`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRowLoading(null);
        }
    };

    const downloadCasePdf = async (caseId, title) => {
        setRowLoading(`${caseId}-pdf`);
        try {
            await downloadBlob(`/vault/case/${caseId}.pdf`, `${title}_SettlementAgreement.pdf`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRowLoading(null);
        }
    };

    const downloadAll = async () => {
        setBulkLoading(true);
        try {
            const url = year ? `/vault/all.zip?year=${year}` : "/vault/all.zip";
            await downloadBlob(url, year ? `ClosedCases_${year}.zip` : "ClosedCases.zip");
            toast.success(year ? `${year} archive downloaded.` : "Full archive downloaded.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBulkLoading(false);
        }
    };

    const signAllPending = async () => {
        if (!pending.count) return;
        setBulkSigning(true);
        try {
            const res = await api.post("/vault/sign-pending.zip", {}, { responseType: "blob" });
            const disp = res.headers["content-disposition"] || "";
            const match = disp.match(/filename="([^"]+)"/);
            const name = match ? match[1] : "LienLiftDirect_SignedLetters.zip";
            const blob = new Blob([res.data], { type: "application/zip" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
            toast.success(`Signed ${pending.count} letter${pending.count === 1 ? "" : "s"}. ZIP downloaded.`);
            // Refresh count — should now be 0
            const fresh = await api.get("/vault/pending-sign");
            setPending(fresh.data);
        } catch (e) {
            // Blob responses need to be re-parsed for error text
            if (e?.response?.data && e.response.data instanceof Blob) {
                const txt = await e.response.data.text();
                try {
                    const j = JSON.parse(txt);
                    toast.error(j.detail || "Bulk sign failed");
                } catch {
                    toast.error("Bulk sign failed");
                }
            } else {
                toast.error(formatApiError(e));
            }
        } finally {
            setBulkSigning(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="vault-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <p className="overline">Closed cases</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Documents</h1>
                    <p className="text-slate-600 mt-3 max-w-2xl">
                        Signed settlement agreements, Stripe payment receipts, and complete audit trails
                        for every closed case — bundled and ready for accountants, credit-deletion follow-ups,
                        and year-end compliance.
                    </p>
                </div>
                <Button
                    onClick={downloadAll}
                    disabled={bulkLoading || data.cases.length === 0}
                    className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-5"
                    data-testid="vault-download-all"
                >
                    <Archive size={16} className="mr-2" weight="duotone" />
                    {bulkLoading
                        ? "Building archive…"
                        : year
                            ? `Download ${year} archive`
                            : "Download full archive"}
                </Button>
            </div>

            {data.cases.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden" data-testid="vault-stats">
                    <Stat icon={VaultIcon} label="Closed cases" value={data.stats.count} tint="blue" />
                    <Stat icon={CheckCircle} label="Total settled" value={`$${data.stats.total_settled_amount.toLocaleString()}`} tint="green" />
                    <Stat icon={CalendarBlank} label="Total savings" value={`$${data.stats.total_savings.toLocaleString()}`} tint="amber" />
                </div>
            )}

            {!pendingLoading && pending.count > 0 && (
                <Card className="p-6 border-2 border-[#0F1E3D] bg-gradient-to-br from-slate-50 to-white" data-testid="pending-sign-card">
                    <div className="flex items-start gap-4">
                        <div className="h-11 w-11 rounded-full bg-[#0F1E3D] text-white flex items-center justify-center shrink-0">
                            <Signature size={20} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <h3 className="font-serif text-xl text-[#0F1E3D]">
                                        Sign all pending
                                        <span
                                            className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-rose-600 text-white text-xs font-mono font-semibold align-middle"
                                            data-testid="pending-sign-badge"
                                        >
                                            {pending.count}
                                        </span>
                                    </h3>
                                    <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
                                        You have <span className="font-medium text-slate-900">{pending.count}</span> unsigned {pending.count === 1 ? "letter" : "letters"} across your active cases.
                                        Apply your saved signature to all of them and download a single ZIP — ready to print, mail, or file.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                                    <Button
                                        onClick={() => setSendDialogOpen(true)}
                                        disabled={bulkSigning}
                                        className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-5"
                                        data-testid="pending-send-btn"
                                    >
                                        <PaperPlaneTilt size={16} className="mr-2" weight="fill" />
                                        Sign &amp; send {pending.count}…
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={signAllPending}
                                        disabled={bulkSigning}
                                        className="h-11 px-4"
                                        data-testid="pending-sign-btn"
                                    >
                                        <Signature size={14} className="mr-1.5" weight="fill" />
                                        {bulkSigning ? "Signing…" : "Sign & download"}
                                    </Button>
                                </div>
                            </div>
                            {pending.preview.length > 0 && (
                                <ul className="mt-4 pt-4 border-t border-slate-200 space-y-1.5 text-xs text-slate-600" data-testid="pending-sign-preview">
                                    {pending.preview.slice(0, 5).map((p) => (
                                        <li key={p.ai_output_id} className="flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                                            <Link to={`/cases/${p.case_id}`} className="font-medium text-slate-800 hover:underline truncate">
                                                {p.case_title}
                                            </Link>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-slate-500">{p.mode_title}</span>
                                            <span className="text-slate-400 ml-auto font-mono">{formatDateUS(p.created_at)}</span>
                                        </li>
                                    ))}
                                    {pending.count > pending.preview.length && (
                                        <li className="text-slate-400 pl-3.5">…and {pending.count - pending.preview.length} more</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by title or creditor…"
                        className="h-10"
                        data-testid="vault-search"
                    />
                </div>
                {years.length > 0 && (
                    <div className="flex items-center gap-2" data-testid="vault-year-filter">
                        <Funnel size={14} className="text-slate-500" />
                        <Button
                            variant={year === "" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setYear("")}
                            className={year === "" ? "bg-[#0F1E3D]" : ""}
                            data-testid="vault-year-all"
                        >
                            All
                        </Button>
                        {years.map((y) => (
                            <Button
                                key={y}
                                variant={year === y ? "default" : "outline"}
                                size="sm"
                                onClick={() => setYear(y)}
                                className={year === y ? "bg-[#0F1E3D]" : ""}
                                data-testid={`vault-year-${y}`}
                            >
                                {y}
                            </Button>
                        ))}
                    </div>
                )}
            </div>

            {loading ? (
                <p className="text-slate-500">Loading…</p>
            ) : filtered.length === 0 ? (
                <Card className="p-12 text-center border-dashed" data-testid="vault-empty">
                    <VaultIcon size={36} className="mx-auto text-slate-400" weight="duotone" />
                    <p className="text-slate-700 mt-4 font-medium">
                        {data.cases.length === 0 ? "No closed cases yet." : "No cases match the current filter."}
                    </p>
                    {data.cases.length === 0 && (
                        <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                            Once a case is marked settled — through an accepted offer or a Stripe payment —
                            its signed agreement, payment receipt, and audit trail land here automatically.
                        </p>
                    )}
                </Card>
            ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="vault-table">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Case</th>
                                <th className="text-left px-4 py-3 font-medium">Creditor</th>
                                <th className="text-right px-4 py-3 font-medium">Settled</th>
                                <th className="text-right px-4 py-3 font-medium">Savings</th>
                                <th className="text-left px-4 py-3 font-medium">Date</th>
                                <th className="text-right px-4 py-3 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => {
                                const date = c.settled_at ? formatDateUS(c.settled_at) : "—";
                                return (
                                    <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`vault-row-${c.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Link to={`/cases/${c.id}`} className="font-medium text-slate-900 hover:underline">
                                                    {c.title || "Untitled"}
                                                </Link>
                                                {c.paid_via_stripe && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium bg-emerald-100 text-emerald-800">
                                                        <CheckCircle size={10} weight="fill" /> Paid
                                                    </span>
                                                )}
                                            </div>
                                            {c.case_number && (
                                                <p className="text-xs text-slate-500 mt-0.5">#{c.case_number}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{c.creditor_name || "—"}</td>
                                        <td className="px-4 py-3 text-right font-mono">${(c.settled_amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right font-mono text-emerald-700">${(c.savings || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-slate-600">{date}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => downloadCasePdf(c.id, c.title || c.id)}
                                                    disabled={rowLoading === `${c.id}-pdf`}
                                                    data-testid={`vault-download-pdf-${c.id}`}
                                                    className="h-8"
                                                >
                                                    <DownloadSimple size={13} className="mr-1.5" />
                                                    {rowLoading === `${c.id}-pdf` ? "…" : "PDF"}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => downloadCase(c.id, c.title || c.id)}
                                                    disabled={rowLoading === c.id}
                                                    data-testid={`vault-download-${c.id}`}
                                                    className="h-8"
                                                >
                                                    <DownloadSimple size={13} className="mr-1.5" />
                                                    {rowLoading === c.id ? "…" : "ZIP"}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Card className="p-6 bg-slate-50/60 border-slate-200" data-testid="digest-card">
                <div className="flex items-start gap-4">
                    <EnvelopeSimple size={24} weight="duotone" className="text-[#0F1E3D] shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <h3 className="font-serif text-xl text-[#0F1E3D]">Auto-mail your archive</h3>
                                <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
                                    On the 1st of each month at 09:00 UTC we email you a ZIP of every case
                                    you closed in the prior month. On January 1 we email the full prior-year archive.
                                    Hand it straight to your accountant.
                                </p>
                            </div>
                            <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer select-none" data-testid="digest-toggle-label">
                                <span>Email digests</span>
                                <Switch
                                    checked={digestOptIn}
                                    onCheckedChange={toggleDigest}
                                    disabled={digestSaving}
                                    data-testid="digest-toggle"
                                />
                            </label>
                        </div>
                        <div className="flex items-center gap-2 mt-4 flex-wrap">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => sendDigestNow("month")}
                                disabled={digestSending !== null}
                                data-testid="digest-send-month"
                                className="h-9"
                            >
                                <PaperPlaneTilt size={13} className="mr-1.5" />
                                {digestSending === "month" ? "Sending…" : "Send last-month digest now"}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => sendDigestNow("year")}
                                disabled={digestSending !== null}
                                data-testid="digest-send-year"
                                className="h-9"
                            >
                                <PaperPlaneTilt size={13} className="mr-1.5" />
                                {digestSending === "year" ? "Sending…" : "Send last-year archive now"}
                            </Button>
                        </div>
                        {digestHistory.length > 0 && (
                            <div className="mt-5 pt-4 border-t border-slate-200" data-testid="digest-history">
                                <p className="overline mb-2">Recent digests</p>
                                <ul className="space-y-1.5 text-xs text-slate-600">
                                    {digestHistory.slice(0, 6).map((h, i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <span className="font-mono text-slate-500">
                                                {formatDateUS(h.at)}{" "}
                                                {new Date(h.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                            </span>
                                            <span className="capitalize">{h.kind}</span>
                                            <span>· {h.label}</span>
                                            <span>· {h.case_count} case{h.case_count === 1 ? "" : "s"}</span>
                                            <span className={h.mocked ? "text-amber-700" : "text-emerald-700"}>
                                                {h.mocked ? "(MOCKED — RESEND_API_KEY missing)" : h.result_status}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            <p className="text-xs text-slate-500 max-w-2xl">
                Each archive includes a signed settlement-agreement PDF, the Stripe payment-transaction record
                (when paid through LienLift Direct), an immutable audit trail, and a JSON snapshot of the case
                at time of export. Not legal advice.
            </p>

            <SendPendingDialog
                open={sendDialogOpen}
                onOpenChange={setSendDialogOpen}
                onComplete={refreshPending}
            />
        </div>
    );
}

function Stat({ icon: Icon, label, value, tint }) {
    const tones = {
        blue: { bg: "bg-blue-50", icon: "text-blue-700" },
        green: { bg: "bg-emerald-50", icon: "text-emerald-700" },
        amber: { bg: "bg-amber-50/80", icon: "text-amber-800" },
    };
    const t = tones[tint] || tones.blue;
    return (
        <div className={`p-6 ${t.bg}`}>
            <Icon size={20} weight="duotone" className={t.icon} />
            <p className="overline mt-3">{label}</p>
            <p className="font-mono text-3xl mt-1 font-semibold">{value}</p>
        </div>
    );
}
